import stringify from 'fast-safe-stringify'

import { SomeEntity } from '~/clients/api-guards'
import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'

import { dispatchErrorToast } from '../slices/toasterSlice'
import { DiEntityKey, useStore } from '../store'
import { AsyncFn, lookupEntity } from './utils'

/**
 * Middleware that updates entities
 * Works by updating the entity in the store before the call, and adding it back if the call fails.
 * This is to provide a better user experience by showing the updated entity immediately.
 */
const onGoingUpdates = new Map<string, Promise<void>>()

export function withOptimisticUpdate<T extends AsyncFn>(call: T, key: DiEntityKey): (...args: Parameters<T>) => Promise<void> {
    return async (...args: Parameters<T>) => {
        if (args.length > 2) {
            console.error('withOptimisticUpdate does not support batch updates, use withOptimisticBatchUpdate instead')
            return
        }

        const [idOrUuid, userUpdatedEntity] = args
        const { entity: storedEntity, numericId } = lookupEntity(key, idOrUuid as number | string)

        // If entity is not in the store, we don't do anything and return a warning
        // This is in order to prevent accidental updates
        if (!storedEntity || numericId === undefined) {
            console.warn(`Trying to update entity with id ${idOrUuid} from ${key}, but it's not in the store`)
            return
        }

        // Optimistically update the entity in the store
        const optimisticEntity = { ...storedEntity, ...userUpdatedEntity, isOptimistic: true }
        useStore.getState().di.actions.replaceEntity(key, numericId, optimisticEntity)

        // regardless whether this entity already has an ongoing update, we always want to inform the api about subsequent PUTs and PATCHes
        const promise = call(...args)

        function onFailure(statusCode?: number) {
            dispatchErrorToast({ message: getErrorMessageByStatusCode(statusCode) })
            useStore.getState().di.actions.removeEntity(key, numericId!)
            if (storedEntity) {
                useStore.getState().di.actions.addEntities([storedEntity])
            }
        }

        let result

        try {
            result = await promise
        } catch (error) {
            onFailure()
            throw error
        }

        if (result?.response?.status === 200) {
            useStore.getState().di.actions.replaceEntity(key, numericId, result.data)
        } else {
            onFailure(result?.response?.status)
        }
    }
}

type BatchUpdateSchema = {
    id: number | string
    body: Record<string, unknown>
}

function isBatchUpdateSchema(arg: unknown): arg is BatchUpdateSchema {
    return typeof arg === 'object' && arg !== null && 'id' in arg && 'body' in arg
}

export function withOptimisticBatchUpdate<T extends AsyncFn>(call: T, key: DiEntityKey): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
    return async (...args: Parameters<T>) => {
        const batch = args[0]
        if (!Array.isArray(batch) || !batch.every(isBatchUpdateSchema)) {
            console.error('withOptimisticBatchUpdate expects an array of { id: number | string, body: Record<string, unknown> } as the arguments')
            return
        }

        if (batch.length === 0) {
            return
        }

        const ids = batch.map(({ id }) => id)

        const currentEntities = ids.map(id => lookupEntity(key, id).entity).filter((e): e is SomeEntity => Boolean(e))

        const currentKey = `${call.name}__${stringify(batch)}`
        if (onGoingUpdates.has(currentKey)) return onGoingUpdates.get(currentKey)

        // Optimistically update the entity in the store
        const optimisticEntities = batch
            .map(({ id, body }) => {
                const { entity: storedEntity } = lookupEntity(key, id)
                if (!storedEntity) {
                    console.warn(`Trying to update entity with id ${id} from ${key}, but it's not in the store`)
                    return null
                }

                return { ...storedEntity, ...body }
            })
            .filter((e): e is SomeEntity => Boolean(e))

        function onFailure(statusCode?: number) {
            // The entities that were in the store before the update are added back
            useStore.getState().di.actions.replaceEntities(key, currentEntities)

            dispatchErrorToast({ message: getErrorMessageByStatusCode(statusCode) })
        }

        // Optimistically update the entities in the store
        useStore.getState().di.actions.replaceEntities(key, optimisticEntities)

        const promise = call(batch)
        onGoingUpdates.set(currentKey, promise)

        let result
        try {
            result = await promise
        } catch (error) {
            onFailure()
            throw error
        } finally {
            onGoingUpdates.delete(currentKey)
        }

        if (result?.response?.status === 200) {
            const data = Array.isArray(result.data) ? result.data : [result.data]
            useStore.getState().di.actions.replaceEntities(key, data)
        } else {
            onFailure(result?.response?.status)
            throw new Error(`Failed to update entities with ids ${ids} from ${key}`)
        }

        return result
    }
}
